import styled, { css } from 'styled-components';

export type ButtonVariant = 'primary' | 'halo' | 'ghost' | 'soft';
export type ButtonSize = 'md' | 'sm';

const variants = {
  primary: css`
    background: ${({ theme }) => theme.ink[1]};
    color: ${({ theme }) => theme.surface.deep};
    &:hover {
      background: ${({ theme }) => (theme.name === 'dark' ? '#FFFFFF' : theme.ink[2])};
      box-shadow: ${({ theme }) => theme.shadows.sh2};
    }
  `,
  halo: css`
    background: ${({ theme }) => theme.tone.halo.base};
    color: ${({ theme }) => theme.surface.elev};
    &:hover {
      background: ${({ theme }) => theme.tone.halo.deep};
      box-shadow: 0 4px 12px ${({ theme }) => theme.fx.ringHalo};
    }
  `,
  ghost: css`
    background: transparent;
    border-color: ${({ theme }) => theme.line.line};
    color: ${({ theme }) => theme.ink[2]};
    &:hover {
      border-color: ${({ theme }) => theme.ink[3]};
      color: ${({ theme }) => theme.ink[1]};
      background: ${({ theme }) => theme.surface.elev};
    }
  `,
  soft: css`
    background: ${({ theme }) => theme.surface.deep};
    color: ${({ theme }) => theme.ink[2]};
    &:hover {
      background: ${({ theme }) => theme.line.soft};
      color: ${({ theme }) => theme.ink[1]};
    }
  `,
};

export const Button = styled.button<{ $variant?: ButtonVariant; $size?: ButtonSize }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme, $size }) =>
    $size === 'sm' ? `${theme.spacing[2]} ${theme.spacing[4]}` : `${theme.spacing[3]} ${theme.spacing[5]}`};
  font-family: ${({ theme }) => theme.typography.fontFamilies.sans};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  font-size: ${({ theme, $size }) =>
    $size === 'sm' ? theme.typography.fontSizes.ui : theme.typography.fontSizes.uiLg};
  border-radius: ${({ theme }) => theme.radii.pill};
  border: 1px solid transparent;
  cursor: pointer;
  white-space: nowrap;
  transition:
    transform ${({ theme }) => theme.motion.duration.fast} ${({ theme }) => theme.motion.easing.out},
    background ${({ theme }) => theme.motion.duration.fast} ${({ theme }) => theme.motion.easing.out},
    box-shadow ${({ theme }) => theme.motion.duration.fast} ${({ theme }) => theme.motion.easing.out},
    border-color ${({ theme }) => theme.motion.duration.fast} ${({ theme }) => theme.motion.easing.out},
    color ${({ theme }) => theme.motion.duration.fast} ${({ theme }) => theme.motion.easing.out};

  &:active {
    transform: translateY(1px);
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    transform: none !important;
  }

  ${({ $variant = 'primary' }) => variants[$variant]}
`;

export const Arrow = styled.span`
  font-family: ${({ theme }) => theme.typography.fontFamilies.serifEd};
  font-style: italic;
  font-size: 1.05em;
  line-height: 0.8;
`;
